import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import {
  Box, Typography, Container, Button, CircularProgress,
  Avatar, IconButton, Divider, Chip,
} from "@mui/material";
import {
  Facebook, Instagram, Twitter, YouTube,
  Person2, PhoneEnabled, LocationOn, EmailIcon,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CakeIcon from "@mui/icons-material/Cake";
import StorefrontIcon from "@mui/icons-material/Storefront";
import config from "../../config";
import { connect } from "react-redux";
import * as Actions from "../../redux/actions/";

const InfoRow = ({ icon, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: "8px", flexShrink: 0,
        backgroundColor: "rgba(255,192,1,0.08)", border: "1px solid rgba(255,192,1,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {React.cloneElement(icon, { sx: { color: "#FFC001", fontSize: 15 } })}
      </Box>
      <Typography sx={{ color: "#bbb", fontSize: "14px" }}>{value}</Typography>
    </Box>
  );
};

const ViewUserProfile = ({ jwtToken, seller, refreshSeller }) => {
  const { userId } = useParams();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    refreshSeller();
    const fetchUser = async () => {
      try {
        const res = await axios.get(`${config.SERVER_URL}/api/users/edit`, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
        });
        if (res.status === 200) setUserProfile(res.data.data);
      } catch {}
      setLoading(false);
    };
    fetchUser();
  }, [jwtToken]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "#111", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: "#FFC001" }} />
      </Box>
    );
  }

  const socials = [
    { url: userProfile?.twitter_url, icon: <Twitter />, label: "Twitter" },
    { url: userProfile?.facebook_url, icon: <Facebook />, label: "Facebook" },
    { url: userProfile?.youtube_url, icon: <YouTube />, label: "YouTube" },
    { url: userProfile?.instagram_url, icon: <Instagram />, label: "Instagram" },
  ].filter(s => s.url);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>

      {/* ── Hero ── */}
      <Box sx={{ position: "relative", background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)", borderBottom: "1px solid #222", py: { xs: 5, md: 7 }, overflow: "hidden" }}>
        <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
            <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>
            <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>Profile</Typography>
          </Box>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: "28px", md: "44px" }, color: "#fff", lineHeight: 1.1 }}>
            My <Box component="span" sx={{ color: "#FFC001" }}>Profile</Box>
          </Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mt: 1 }}>
            Only visible to sellers you choose to interact with.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {userProfile ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "280px 1fr" }, gap: 3, alignItems: "start" }}>

            {/* ── Left: avatar + actions ── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3, textAlign: "center" }}>
                <Avatar
                  src={userProfile.user_profile?.url || userProfile.avatar_url}
                  alt={userProfile.full_name}
                  sx={{ width: 100, height: 100, mx: "auto", mb: 2, border: "3px solid rgba(255,192,1,0.25)", boxShadow: "0 0 20px rgba(255,192,1,0.1)" }}
                />
                <Typography sx={{ fontWeight: 800, fontSize: "17px", color: "#fff", mb: 0.3 }}>
                  {userProfile.full_name}
                </Typography>
                {userProfile.brandName && (
                  <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>
                    {userProfile.brandName}
                  </Typography>
                )}
                {userProfile.city && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mt: 1 }}>
                    <LocationOn sx={{ color: "#555", fontSize: 14 }} />
                    <Typography sx={{ color: "#666", fontSize: "12px" }}>{userProfile.city}</Typography>
                  </Box>
                )}
              </Box>

              {/* Action buttons */}
              <Button
                component={Link}
                to={!seller ? "/seller/register" : "/seller/profile"}
                fullWidth
                sx={{
                  py: 1.1, borderRadius: "10px", backgroundColor: "#FFC001",
                  color: "#000", fontWeight: 800, fontSize: "13px", textTransform: "none",
                  "&:hover": { backgroundColor: "#e6ac00" },
                }}
                startIcon={<StorefrontIcon sx={{ fontSize: "17px !important" }} />}
              >
                {!seller ? "Register As Seller" : "Seller Dashboard"}
              </Button>

              <Button
                component={Link}
                to={`/users/${userId}/edit`}
                fullWidth
                sx={{
                  py: 1.1, borderRadius: "10px", border: "1px solid #2a2a2a",
                  color: "#aaa", fontWeight: 700, fontSize: "13px", textTransform: "none",
                  "&:hover": { borderColor: "#FFC001", color: "#fff", backgroundColor: "rgba(255,192,1,0.04)" },
                }}
                startIcon={<EditIcon sx={{ fontSize: "16px !important" }} />}
              >
                Edit Profile
              </Button>

              {/* Socials */}
              {socials.length > 0 && (
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 2.5 }}>
                  <Typography sx={{ color: "#555", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5 }}>Social</Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {socials.map(({ url, icon, label }) => (
                      <Link key={label} to={url} target="_blank" rel="noopener noreferrer">
                        <IconButton sx={{
                          width: 36, height: 36, border: "1px solid #2a2a2a", color: "#666",
                          borderRadius: "9px",
                          "&:hover": { borderColor: "#FFC001", color: "#FFC001", backgroundColor: "rgba(255,192,1,0.06)" },
                        }}>
                          {React.cloneElement(icon, { sx: { fontSize: 17 } })}
                        </IconButton>
                      </Link>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            {/* ── Right: details ── */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

              {/* Contact info */}
              <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                <Typography sx={{ color: "#555", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", mb: 2 }}>Contact Info</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <InfoRow icon={<EmailOutlinedIcon />} value={userProfile.email} />
                  <InfoRow icon={<PhoneEnabled />} value={userProfile.phone} />
                  <InfoRow icon={<CakeIcon />} value={userProfile.date_of_birth} />
                </Box>
              </Box>

              {/* About */}
              {userProfile.about && (
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3 }}>
                  <Typography sx={{ color: "#555", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", mb: 2 }}>About</Typography>
                  <Typography sx={{ color: "#888", fontSize: "14px", lineHeight: 1.9 }}>
                    {userProfile.about}
                  </Typography>
                </Box>
              )}

              {/* Empty state if no info */}
              {!userProfile.about && !userProfile.phone && !userProfile.date_of_birth && (
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px dashed #2a2a2a", borderRadius: "16px", p: 4, textAlign: "center" }}>
                  <Typography sx={{ color: "#444", fontSize: "14px" }}>
                    Your profile is looking a bit empty. <Link to={`/users/${userId}/edit`} style={{ color: "#FFC001", textDecoration: "underline" }}>Add more info</Link> to help sellers know who you are.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography sx={{ color: "#555", fontSize: "15px" }}>Could not load profile.</Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

const mapStateToProps = ({ Auth, Seller }) => ({
  jwtToken: Auth.jwtToken,
  seller: Seller.seller,
});
const mapDispatchToProps = (dispatch) => ({
  refreshSeller: () => dispatch(Actions.thunkGetSeller()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ViewUserProfile);
